import { useMemo, useCallback } from 'react'

import { patchAccountLightweightSettings, PatchOperation } from 'redux/features/settings/settingsApiThunks'
import { AvailableSettings, BrtsExemptEntry, DomainEntry } from 'types/Settings'
import { useAppDispatch, useAppSelector } from 'redux/toolkit/hooks'
import { isPending } from 'redux/toolkit/api'
import routesConfig from 'lib/routesConfig'

export interface State {
  isLoading: boolean
  tableData: DomainEntry[]
}

export interface EventHandlers {
  onAddItem: (item: DomainEntry) => void
  onRemoveItem: (id: string) => void
  onBulkEdit: () => void
}

export type UseBrtsExemptLogic = [State, EventHandlers]

export const useBrtsExemptLogic = (): UseBrtsExemptLogic => {
  const dispatch = useAppDispatch()
  const { accountSettings, updateAccountSettingPending } = useAppSelector(_stores => ({
    accountSettings: _stores.settings.accountSettings,
    updateAccountSettingPending: isPending(_stores.settings.updateAccountSettingsApiStatus)
  }))

  const brtsExempt = useMemo(() => {
    const exemptData = accountSettings.brts_exempt
    if (!exemptData) return []
    if (Array.isArray(exemptData)) return exemptData as BrtsExemptEntry[]
    if (typeof exemptData === 'string') {
      try {
        return JSON.parse(exemptData) as BrtsExemptEntry[]
      } catch {
        return []
      }
    }
    return []
  }, [accountSettings])

  const tableData = useMemo(
    () =>
      brtsExempt.map(entry => ({
        domain: entry.name,
        comment: entry.comment,
        id: entry.id
      })) as DomainEntry[],
    [brtsExempt]
  )

  const onAddItem = useCallback(
    (item: DomainEntry) => {
      dispatch(
        patchAccountLightweightSettings({
          operation: PatchOperation.ADD,
          value: {
            name: item.domain,
            comment: item.comment,
            id: item.id
          },
          variable: AvailableSettings.BRTS_EXEMPT
        })
      )
    },
    [dispatch]
  )

  const onRemoveItem = useCallback(
    (id: string) => {
      dispatch(
        patchAccountLightweightSettings({
          operation: PatchOperation.REMOVE,
          value: { id },
          variable: AvailableSettings.BRTS_EXEMPT
        })
      )
    },
    [dispatch]
  )

  const onBulkEdit = useCallback(() => {
    routesConfig.INBOUND_SETTINGS_BRTS_EXEMPT_BULK_EDIT.goto()
  }, [])

  return useMemo(
    () => [
      {
        isLoading: updateAccountSettingPending,
        tableData
      },
      {
        onAddItem,
        onRemoveItem,
        onBulkEdit
      }
    ],
    [updateAccountSettingPending, tableData, onAddItem, onRemoveItem, onBulkEdit]
  )
}
